import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  INSTALLMENT_OPTIONS,
  MIN_INSTALLMENTS,
} from "@/constants/payroll-installments";
import { DivergenceStatusEnum } from "@/enum/divergence-status";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { formatDate } from "@/utils/format-date";
import { AlertTriangle, Check, X } from "lucide-react";
import { useState } from "react";

import {
  BATCH_PREFLIGHT_LABELS,
  DECISION_LABELS,
} from "../../utils/messages";
import { amountToneOf, formatAmount } from "../../utils/amount-display";
import {
  BatchCandidate,
  BatchPreflight,
  BatchSkipped,
  buildBatchPreflight,
} from "../../utils/batch-preflight";
import { isPayrollDiscount } from "../../utils/is-payroll-discount";

const requestedStatusOf = (action: "approve" | "reject"): DivergenceStatusEnum =>
  action === "approve"
    ? DivergenceStatusEnum.APPROVED
    : DivergenceStatusEnum.REJECTED;

export const BATCH_PREFLIGHT_TEST_ID: string = "batch-preflight";
export const BATCH_TO_PAYROLL_TEST_ID: string = "batch-to-payroll";
export const BATCH_SKIPPED_TEST_ID: string = "batch-skipped";
export const BATCH_INSTALLMENTS_TEST_ID: string = "batch-installments";
export const BATCH_APPLY_ALL_TEST_ID: string = "batch-installments-apply-all";

export interface BatchDecisionItem {
  id: number;
  installments_count?: number;
}

export interface BatchItem extends BatchCandidate {
  number?: string | null;
  amount?: string | number | null;
  operator?: string | null;
  store_code?: number | string | null;
  conciliation?: { date?: string; store?: { code?: number | string } };
}

interface BatchActionModalProps {
  action: "approve" | "reject";
  items: BatchItem[];
  dataType: "divergence" | "nsu";
  onConfirm: (decisions: BatchDecisionItem[]) => Promise<void>;
}

const emptyPreflight = (items: BatchItem[]): BatchPreflight => ({
  valid: items,
  skipped: [],
  payrollDiscountCount: 0,
});

export const BatchActionModal = ({
  action,
  items,
  dataType,
  onConfirm,
}: BatchActionModalProps) => {
  const { handleModal } = useDialogModal();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [installmentsById, setInstallmentsById] = useState<
    Record<number, number>
  >({});

  const isApprove: boolean = action === "approve";
  const isDivergence: boolean = dataType === "divergence";
  const actionLabel: string = isApprove
    ? DECISION_LABELS.APPROVE
    : DECISION_LABELS.REJECT;

  const preflight: BatchPreflight = isDivergence
    ? buildBatchPreflight(items, requestedStatusOf(action))
    : emptyPreflight(items);

  const passing: BatchItem[] = preflight.valid as BatchItem[];
  const count: number = passing.length;

  const typeLabel: string = isDivergence
    ? count === 1
      ? "divergência"
      : "divergências"
    : count === 1
      ? "NSU"
      : "NSUs";

  const needsInstallments = (item: BatchItem): boolean =>
    isApprove && isDivergence && isPayrollDiscount(item);

  const installmentsOf = (item: BatchItem): number =>
    installmentsById[item.id] ?? MIN_INSTALLMENTS;

  const parceledItems: BatchItem[] = passing.filter(needsInstallments);

  const applyToAll = (value: number): void => {
    setInstallmentsById(
      (current: Record<number, number>): Record<number, number> => ({
        ...current,
        ...Object.fromEntries(
          parceledItems.map((item: BatchItem): [number, number] => [
            item.id,
            value,
          ]),
        ),
      }),
    );
  };

  const handleCancel = (): void => {
    handleModal({ isOpen: false });
  };

  const handleConfirm = async (): Promise<void> => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      await onConfirm(
        passing.map(
          (item: BatchItem): BatchDecisionItem => ({
            id: item.id,
            ...(needsInstallments(item)
              ? { installments_count: installmentsOf(item) }
              : {}),
          }),
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-hidden flex flex-col">
      <div
        className={`flex items-center gap-2 ${isApprove ? "text-green-600" : "text-amber-600"}`}
      >
        {isApprove ? (
          <Check className="h-5 w-5" />
        ) : (
          <AlertTriangle className="h-5 w-5" />
        )}
        <span className="font-medium">
          {BATCH_PREFLIGHT_LABELS.CONFIRM_TITLE(actionLabel)}
        </span>
      </div>

      <p className="text-sm text-gray-600">
        {BATCH_PREFLIGHT_LABELS.INTRO(actionLabel, count, typeLabel)}
      </p>

      {isDivergence && isApprove && (
        <p
          className="text-sm font-medium text-[#613F4F] bg-amber-50 border border-amber-200 rounded-lg p-3"
          data-testid={BATCH_TO_PAYROLL_TEST_ID}
        >
          {preflight.payrollDiscountCount > 0
            ? BATCH_PREFLIGHT_LABELS.TO_PAYROLL(preflight.payrollDiscountCount)
            : BATCH_PREFLIGHT_LABELS.NONE_TO_PAYROLL}
        </p>
      )}

      {parceledItems.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3">
          <span className="text-xs text-gray-600">
            {DECISION_LABELS.INSTALLMENTS_BATCH_HINT}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {DECISION_LABELS.INSTALLMENTS_BATCH_APPLY_ALL}
            </span>
            <Select
              onValueChange={(value: string): void => applyToAll(Number(value))}
            >
              <SelectTrigger
                className="h-8 w-[80px]"
                data-testid={BATCH_APPLY_ALL_TEST_ID}
                aria-label={DECISION_LABELS.INSTALLMENTS_BATCH_APPLY_ALL}
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {INSTALLMENT_OPTIONS.map(
                  (option: number): JSX.Element => (
                    <SelectItem key={option} value={`${option}`}>
                      {`${option}x`}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div
        className="flex-1 overflow-y-auto space-y-2 max-h-[40vh] pr-2"
        data-testid={BATCH_PREFLIGHT_TEST_ID}
      >
        <span className="text-xs font-medium text-gray-500 uppercase">
          {BATCH_PREFLIGHT_LABELS.WILL_PASS} ({count})
        </span>

        {count === 0 && (
          <p className="text-sm text-amber-700">
            {BATCH_PREFLIGHT_LABELS.NOTHING_TO_PROCESS}
          </p>
        )}

        {passing.map((item: BatchItem) => (
          <div key={item.id} className="p-3 border rounded-lg bg-gray-50 text-sm">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div className="font-medium text-[#613F4F]">
                  {isDivergence
                    ? `${item.name || BATCH_PREFLIGHT_LABELS.DIVERGENCE} #${item.id}`
                    : `NSU ${item.number || ""} #${item.id}`}
                </div>
                <div className="text-xs text-gray-500 space-y-0.5">
                  <div>
                    {BATCH_PREFLIGHT_LABELS.STORE}{" "}
                    {item.store_code || item.conciliation?.store?.code || "-"}
                  </div>
                  {item.operator && (
                    <div>
                      {BATCH_PREFLIGHT_LABELS.OPERATOR} {item.operator}
                    </div>
                  )}
                  <div>
                    {BATCH_PREFLIGHT_LABELS.DATE}{" "}
                    {formatDate(item.conciliation?.date, { fallback: "-" })}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {needsInstallments(item) && (
                  <Select
                    value={`${installmentsOf(item)}`}
                    onValueChange={(value: string): void =>
                      setInstallmentsById(
                        (
                          current: Record<number, number>,
                        ): Record<number, number> => ({
                          ...current,
                          [item.id]: Number(value),
                        }),
                      )
                    }
                  >
                    <SelectTrigger
                      className="h-8 w-[72px]"
                      data-testid={`${BATCH_INSTALLMENTS_TEST_ID}-${item.id}`}
                      aria-label={`${DECISION_LABELS.INSTALLMENTS_LABEL} #${item.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INSTALLMENT_OPTIONS.map(
                        (option: number): JSX.Element => (
                          <SelectItem key={option} value={`${option}`}>
                            {`${option}x`}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                )}
                <div className={`font-semibold ${amountToneOf(item.amount)}`}>
                  {formatAmount(item.amount)}
                </div>
              </div>
            </div>
          </div>
        ))}

        {preflight.skipped.length > 0 && (
          <div className="pt-2 space-y-2" data-testid={BATCH_SKIPPED_TEST_ID}>
            <span className="text-xs font-medium text-gray-500 uppercase">
              {BATCH_PREFLIGHT_LABELS.WILL_SKIP} ({preflight.skipped.length})
            </span>

            {preflight.skipped.map((skipped: BatchSkipped) => (
              <div
                key={skipped.item.id}
                className="p-3 border border-dashed rounded-lg bg-white text-sm opacity-70"
              >
                <div className="flex justify-between items-center gap-3">
                  <span className="font-medium text-gray-600">
                    {skipped.item.name || BATCH_PREFLIGHT_LABELS.DIVERGENCE} #
                    {skipped.item.id}
                  </span>
                  <span className="text-xs text-amber-700">
                    {BATCH_PREFLIGHT_LABELS.SKIP_REASON[skipped.reason]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <span className="text-sm text-gray-500">
          {BATCH_PREFLIGHT_LABELS.TOTAL}: {count}{" "}
          {count === 1
            ? BATCH_PREFLIGHT_LABELS.ITEM
            : BATCH_PREFLIGHT_LABELS.ITEMS}
        </span>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {DECISION_LABELS.CANCEL}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || count === 0}
            className={
              isApprove
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
            }
          >
            {isSubmitting ? (
              DECISION_LABELS.PROCESSING
            ) : (
              <>
                {isApprove ? (
                  <Check className="mr-2 h-4 w-4" />
                ) : (
                  <X className="mr-2 h-4 w-4" />
                )}
                {actionLabel} ({count})
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
